import React, { useState } from 'react';
import styled from 'styled-components';
import { SectionContainer, SectionTitle } from '../../styles/components';

const ServicesWrapper = styled.div`
  max-width: var(--container-width);
  margin: 0 auto;
  padding: 0 var(--spacing-md);
`;

const ServiceIntro = styled.div`
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  text-align: center;
`;

const ServiceDescription = styled.p`
  font-size: 1.125rem;
  color: var(--secondary);
  margin-bottom: var(--spacing-lg);
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div<{ $isActive: boolean }>`
  background-color: ${({ $isActive }) => $isActive ? 'rgba(255, 255, 255, 0.05)' : 'transparent'};
  border: 1px solid var(--accent-1);
  padding: var(--spacing-lg);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    transform: translateY(-5px);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background-color: var(--accent-2);
    opacity: ${({ $isActive }) => $isActive ? '1' : '0'};
    transition: opacity 0.3s ease;
  }
`;

const ServiceIcon = styled.div`
  margin-bottom: var(--spacing-md);
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 0;
  
  svg {
    width: 30px;
    height: 30px;
    color: var(--accent-2);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-sm);
`;

const ServiceContent = styled.div`
  height: 0;
  overflow: hidden;
  transition: height 0.3s ease;
  
  &.active {
    height: auto;
    margin-top: var(--spacing-md);
  }
`;

const ServiceText = styled.p`
  color: var(--secondary);
  margin-bottom: var(--spacing-sm);
`;

const ServiceFeatures = styled.ul`
  margin-top: var(--spacing-sm);
  
  li {
    margin-bottom: var(--spacing-xs);
    position: relative;
    padding-left: var(--spacing-md);
    
    &:before {
      content: '•';
      position: absolute;
      left: 0;
      color: var(--accent-2);
    }
  }
`;

interface ServiceItem {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  features: string[];
}

const Services: React.FC = () => {
  const [activeService, setActiveService] = useState<number | null>(null);

  const toggleService = (id: number) => {
    if (activeService === id) {
      setActiveService(null);
    } else {
      setActiveService(id);
    }
  };

  const serviceItems: ServiceItem[] = [
    {
      id: 1,
      title: 'European & Japanese Auto Service',
      description: 'Comprehensive maintenance and repair services for premium European and Japanese vehicles.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 4H3C2.44772 4 2 4.44772 2 5V15C2 15.5523 2.44772 16 3 16H17C17.5523 16 18 15.5523 18 15V5C18 4.44772 17.5523 4 17 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 8L18 10.5V9.5L22 7V8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M6 20H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 16V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'Factory-level diagnostics and repair for European luxury brands',
        'Specialized care for Japanese performance vehicles',
        'Preventative maintenance programs',
        'Dealership alternative with better value'
      ]
    },
    {
      id: 2,
      title: 'Mechanical & Electrical Diagnostics',
      description: 'Advanced diagnostic solutions to identify and resolve complex mechanical and electrical issues.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 21V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5V21L12 17L5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 10L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 7V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'Computer-aided diagnostic equipment',
        'Electrical system troubleshooting',
        'Engine performance analysis',
        'Sensor and component testing'
      ]
    },
    {
      id: 3,
      title: 'Programming & Encoding',
      description: 'Expert vehicle programming services using manufacturer-specific technologies.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 9L12 5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 5V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M20 19H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'ECU programming and coding',
        'Vehicle module encoding',
        'Software updates and upgrades',
        'Custom feature activation'
      ]
    },
    {
      id: 4,
      title: 'Engine Rebuilding & Performance',
      description: 'Complete engine rebuilding services and performance enhancements for enthusiasts.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 7C14 7 15 7 15 8C15 9 14 13 14 13M14 13C14 13 14 14 13 14C12 14 10.0315 14 9 14C7.96851 14 7 13 7 13M14 13L17 19M7 13L4 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 6L9 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 6V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 6V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 14V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'Complete engine rebuilds',
        'Performance upgrades',
        'Precision machining',
        'Dyno tuning and calibration'
      ]
    },
    {
      id: 5,
      title: 'Vehicle Modifications',
      description: 'Custom modification services to enhance your vehicle\'s performance and aesthetics.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.7519 11.1679L11.5547 9.03647C10.8901 8.59343 10 9.06982 10 9.86852V14.1315C10 14.9302 10.8901 15.4066 11.5547 14.9635L14.7519 12.8321C15.3457 12.4362 15.3457 11.5638 14.7519 11.1679Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
      features: [
        'Performance upgrades',
        'Suspension enhancements',
        'Aesthetic modifications',
        'Custom exhaust systems'
      ]
    },
    {
      id: 6,
      title: 'JDM & Classic Vehicles',
      description: 'Aside from euro\'s we have an extensive background in the jdm tuning and drifting world. From custom track focused vehicles to restoring Japanese classics.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M21 7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 7L12 3L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'JDM tuning and modifications',
        'Drift car preparation and setup',
        'Classic Japanese car restoration',
        'Track-focused vehicle builds'
      ]
    },
    {
      id: 7,
      title: 'AC Recharge & Diagnosis',
      description: 'Complete air conditioning system services including refrigerant recharge, leak detection, and comprehensive climate control diagnostics.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 14.76V16.5C14 17.33 13.33 18 12.5 18S11 17.33 11 16.5V14.76C10.39 14.44 10 13.78 10 13C10 11.9 10.9 11 12 11S14 11.9 14 13C14 13.78 13.61 14.44 14 14.76Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 2C6.48 2 2 6.48 2 12S6.48 22 12 22 22 17.52 22 12 17.52 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 6V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 16V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4.22 4.22L5.64 5.64" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M18.36 18.36L19.78 19.78" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'AC system refrigerant recharge',
        'Leak detection and repair',
        'Climate control diagnostics',
        'Compressor and component testing',
        'Performance optimization'
      ]
    },
    {
      id: 8,
      title: 'Auto Glass Services',
      description: 'Professional glass replacement and repair services for all makes and models, ensuring optimal visibility and safety with factory-grade materials.',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      features: [
        'Windshield replacement and repair',
        'Advanced driver assistance system (ADAS) calibration',
        'Side and rear window glass replacement',
        'Factory-grade glass installation'
      ]
    }
  ];

  return (
    <SectionContainer id="services">
      <ServicesWrapper>
        <ServiceIntro>
          <SectionTitle>Our Specialized Services</SectionTitle>
          <ServiceDescription>
            At GS Autohaus, we deliver precision service for European and Japanese vehicles
            with factory-trained expertise and state-of-the-art equipment.
          </ServiceDescription>
        </ServiceIntro>

        <ServicesGrid>
          {serviceItems.map((service) => (
            <ServiceCard
              key={service.id}
              $isActive={activeService === service.id}
              onClick={() => toggleService(service.id)}
            >
              <ServiceIcon>{service.icon}</ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceText>{service.description}</ServiceText>

              <ServiceContent className={activeService === service.id ? 'active' : ''}>
                <ServiceFeatures>
                  {service.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ServiceFeatures>
              </ServiceContent>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesWrapper>
    </SectionContainer>
  );
};

export default Services; 